import { Box, Card, CardBody, CardHeader, CheckBox, Form, FormField, RangeInput, Text } from "grommet"
import { Wind } from "../icons/Wind"
import { Resources, SettingsOption } from "grommet-icons"
import { memo, useEffect, useState } from "react"
import { useSettingsContext } from "./SettingsContextProvider"

const loadFromLocalStorage = () => {
    const settings = localStorage.getItem('settings');
    return settings ? JSON.parse(settings) : null;
}

export const SettingsBox = () => {

    const [settings, setSettings] = useState(loadFromLocalStorage() ? loadFromLocalStorage() : {
        intensity: 1,
        fallSpeed: 0.6,
        windDirection: -0.2,
        snowSize: 4,
        enableSnowDrifts: true,
        maxDriftHeight: 5,
        meltSimulation: true,
        meltSpeed: 0.1,
        weatherSim: false
    });

    const { setSettingsContext } = useSettingsContext();

    useEffect(() => {
        localStorage.setItem('settings', JSON.stringify(settings));
        setSettingsContext(settings);
    }, [settings])

    const handleFormChange = (value) => {
        setSettings(value);
        setSettingsContext(value);
    }

    return (
        <Box>
            <Form value={settings} onChange={handleFormChange}>
                <Box gap={'medium'}>
                    <PhysicsSettingsCard />
                    <VisualsSettingsCard />
                    <AccumulationSettingsCard />
                </Box>
            </Form>
        </Box>
    )
}

const PhysicsSettingsCard = memo(() => {

    const { settingsContext } = useSettingsContext();

    return (
        <SettingsCard title={'Physics'} icon={<Wind />}>
            <FormField label="Enable Weather Simulation" noBorder help="Simulates weather change with different random states: calm, light snow, medium snow, storm.">
                <CheckBox name="weatherSim" toggle />
            </FormField>
            <FormFieldWithValue value={settingsContext?.intensity} name={'intensity'} label={'Intensity'} min={0} max={100} step={1} />
            <FormFieldWithValue value={settingsContext?.fallSpeed} name={'fallSpeed'} label={'Fall Speed'} min={0.1} max={5} step={0.1} />
            <FormFieldWithValue value={settingsContext?.windDirection} name={'windDirection'} label={'Wind Direction'} min={-5} max={5} step={0.1} />
        </SettingsCard>
    )
})

const VisualsSettingsCard = () => {

    const { settingsContext } = useSettingsContext();

    return (
        <SettingsCard title={'Visuals'} icon={<SettingsOption />}>
            <FormFieldWithValue value={settingsContext?.snowSize} name={'snowSize'} label={'Snow Size'} min={1} max={8} step={1} />
        </SettingsCard>
    )
}

const AccumulationSettingsCard = () => {

    const { settingsContext } = useSettingsContext();

    return (
        <SettingsCard title={'Accumulation'} icon={<Resources />}>
            <FormField label="Enable Snow Drifts" noBorder>
                <CheckBox name="enableSnowDrifts" toggle />
            </FormField>
            <FormFieldWithValue value={`${settingsContext?.maxDriftHeight}%`} name={'maxDriftHeight'} label={'Max Drift Height'} min={0} max={100} step={1} />
            <FormField label="Enable Melt Simulation" noBorder help="Simulates snow melting for some time.">
                <CheckBox name="meltSimulation" toggle />
            </FormField>
            <FormFieldWithValue value={settingsContext?.meltSpeed} name={'meltSpeed'} label={'Melt Speed'} min={0} max={20} step={0.1} />
        </SettingsCard>
    )
}

const SettingsCard = ({ title, icon, children }) => {

    return (
        <Card>
            <CardHeader pad={{ top: 'medium', left: 'medium', right: 'medium' }}><Box direction="row" gap={'xsmall'} align="center">{icon}{title}</Box></CardHeader>
            <CardBody>{children}</CardBody>
        </Card>
    )
}

const FormFieldWithValue = ({ value, name, label, min, max, step }) => {

    return (
        <Box direction="row" gap={'small'} fill>
            <Box basis="95%">
                <FormField label={label} noBorder noFocus width={'100%'}>
                    <RangeInput name={name} min={min} max={max} step={step} />
                </FormField>
            </Box>
            <Box basis="5%">
                {value && <Text size="xsmall" weight={600}>{value}</Text>}
            </Box>
        </Box>
    )
}